import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {  getInvestiments, 
    getInvestimentDetails,
    getInvestmentApprovalStatus,
    resubmitInvestment,
    updateInvestment,
    type InvestmentUpdatePayload  } from "../services/applications-services";

import {  Investment, TApiResponse, TErrorMessage, TPager, TSuccess  } from "@/types";
import toast from "react-hot-toast";

export const QUERY_KEY = "investment";

export function useGetInvestments(options: {
    search?: string,
    pager?: TPager,
    filter?: Record<string, unknown>
})  {
    const { isLoading, isFetching, data } = useQuery({
        queryKey: ["QUERY_KEY", options.search, {...options.pager, ...options.filter}],
        queryFn: () => getInvestiments(options),
    });

    return { isLoading, isFetching, investiments: data }
}


export function useGetInvestmentDetails(applicationId: string) {
    const { isLoading, isFetching, data,isStale } = useQuery({
        queryKey: ["investment-details", applicationId],
        queryFn: () => getInvestimentDetails(applicationId),
    });

    return { isLoading, isFetching, investiment: data ,isStale}
}
/**
 * Approval workflow status for an investment application — legal review,
 * inspection and the final decision, each with the reviewer's own comment.
 *
 * The endpoint errors for applications that were never pushed into the approval
 * workflow, so failures are surfaced (not toasted) and the panel renders an
 * honest empty state instead of retrying.
 */
export function useGetInvestmentApprovalStatus(applicationId: string) {
    const { isLoading, isFetching, isError, data } = useQuery({
        queryKey: ["investment-approval-status", applicationId],
        queryFn: () => getInvestmentApprovalStatus(applicationId),
        enabled: !!applicationId,
        retry: false,
    });

    return { isLoading, isFetching, isError, approval_status: data }
}

/**
 * Resubmit an investment application a reviewer returned for correction.
 *
 * The backend answers 409 CONFLICT when the application is not actually in
 * RETURNED_FOR_CORRECTION, with a message naming the state it is in — that
 * message is surfaced rather than a generic failure.
 *
 * The list query is keyed on the literal string "QUERY_KEY" (see
 * useGetInvestments above), so that is what has to be invalidated.
 */
export function useResubmitInvestment(applicationId: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: () => resubmitInvestment(applicationId),
        mutationKey: ["resubmit-investment", applicationId],
        onSuccess: (res) => {
            toast.success(res.message ?? "Application resubmitted successfully");
            queryClient.invalidateQueries({ queryKey: ["investment-details", applicationId] });
            queryClient.invalidateQueries({ queryKey: ["investment-approval-status", applicationId] });
            queryClient.invalidateQueries({ queryKey: ["QUERY_KEY"] });
        },
        onError: (error: TErrorMessage) => {
            toast.error(error.response?.data.message ?? "Failed to resubmit application");
        },
    })
}

/**
 * Save an edit to an investment application.
 *
 * The API's own `{ message }` is surfaced verbatim rather than a generic
 * failure, so a refusal the applicant can act on is not swallowed.
 *
 * The list query is keyed on the literal string "QUERY_KEY" (see
 * useGetInvestments above), so that is what has to be invalidated.
 */
export function useUpdateInvestment(
    applicationId: string,
    onSuccess?: TSuccess<TApiResponse<Investment>>
) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: InvestmentUpdatePayload) => updateInvestment(applicationId, data),
        mutationKey: ["update-investment", applicationId],
        onSuccess: (res) => {
            toast.success(res.message ?? "Application updated successfully");
            queryClient.invalidateQueries({ queryKey: ["investment-details", applicationId] });
            queryClient.invalidateQueries({ queryKey: ["QUERY_KEY"] });
            onSuccess?.(res);
        },
        onError: (error: TErrorMessage) => {
            toast.error(error.response?.data.message ?? "Failed to update application");
        },
    })
}
